import { ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '../../catalyst/button';

type EmptyCategoriesStateProps = {
  reason: 'uninitialized' | 'generic';
  canImportModel: boolean;
  canCreateCategory: boolean;
  isImporting: boolean;
  onImportModel: () => void;
  onCreateCategory: () => void;
};

export function EmptyCategoriesState({
  reason,
  canImportModel,
  canCreateCategory,
  isImporting,
  onImportModel,
  onCreateCategory,
}: EmptyCategoriesStateProps) {
  const copy =
    reason === 'uninitialized'
      ? {
          title: 'No classification model loaded',
          description:
            'This environment does not have any categories yet. Import a model to load categories, clusters, and classification data.',
          note: canImportModel
            ? canCreateCategory
              ? 'You can also add categories manually, but classification tooling stays limited until a model is loaded.'
              : 'Import a model to initialize categories and classification tooling in this environment.'
            : canCreateCategory
              ? 'Model import is admin-only. You can still add categories manually if you need to start from scratch.'
              : 'Ask an admin to import a model if this environment should be initialized.',
        }
      : {
          title: 'No categories yet',
          description: 'Import a model export or add your first category to start managing classification categories.',
          note: canImportModel
            ? canCreateCategory
              ? 'If this environment should already have a trained model, import the latest export first.'
              : 'Import a model if this environment should already have categories.'
            : canCreateCategory
              ? 'Model import is admin-only. You can start by adding categories manually.'
              : 'Ask an admin to import a model if this environment should already be initialized.',
        };

  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center shadow dark:border-zinc-700 dark:bg-zinc-800">
      <div className="mx-auto flex max-w-2xl flex-col items-center gap-4">
        <div className="rounded-full bg-gray-100 p-3 text-gray-600 dark:bg-zinc-700 dark:text-zinc-200">
          <ArrowUpTrayIcon className="size-6" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">{copy.title}</h2>
          <p className="text-sm text-gray-500 dark:text-zinc-400">{copy.description}</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {canImportModel && (
            <Button color="indigo" onClick={onImportModel} disabled={isImporting}>
              <ArrowUpTrayIcon />
              {isImporting ? 'Importing...' : 'Import model'}
            </Button>
          )}
          {canCreateCategory && (
            <Button outline onClick={onCreateCategory}>
              <PlusIcon />
              Add category manually
            </Button>
          )}
        </div>
        <p className="max-w-xl text-xs text-gray-500 dark:text-zinc-400">{copy.note}</p>
      </div>
    </div>
  );
}